import { useMemo, useState } from 'react';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import { IssueStatus, InspectionIssue, STATUS_LABEL, STATUS_COLOR, TRADE_COLOR,tradeGroup,TRADE_LABEL } from "types/issue"
import { ChevronLeft, ChevronRight, Search } from '@mui/icons-material';


const tradeInitial = (tradeGroup: string | null) =>
  (tradeGroup?.trim()?.[0] || '?').toUpperCase();


const formatSyncedAt = (isoDate: string) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(isoDate).getTime()) / 1000));
  if (seconds < 60) return `${seconds} sec ago`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min ago`;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];


type DeficienciesSidebarProps = {
  issues: InspectionIssue[];
  total: number;
  lastSyncedAt?: string;
  onView?: (issue: InspectionIssue) => void;
  onAssign?: (issue: InspectionIssue) => void;
};

export const DeficienciesSidebar = ({
  issues,
  total,
  lastSyncedAt,
  onView,
  onAssign
}: DeficienciesSidebarProps) => {
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const tradeOptions = useMemo(() => {
    const trades = new Set<string>();
    issues.forEach((issue) => issue.tradeGroup && trades.add(issue.tradeGroup));
    return Array.from(trades).sort();
  }, [issues]);

  const filtered = useMemo(() => {
    return issues.filter((issue) => {
      const matchesSearch =
        !search?.trim() || issue.title?.toLowerCase().includes(search?.trim().toLowerCase()) || issue.id?.toString().includes(search?.trim())
        // formatDeficiencyId(issue.floorPlanId, issue.id).toLowerCase().includes(search.trim().toLowerCase());
      const matchesTrade = tradeFilter === 'ALL' || issue.tradeGroup === tradeFilter;
      const matchesStatus = statusFilter === 'ALL' || issue.status === statusFilter;
      return matchesSearch && matchesTrade && matchesStatus;
    });
  }, [issues, search, tradeFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage = Math.min(page, pageCount);
  const pageStart = (clampedPage - 1) * pageSize;
  const pageIssues = filtered.slice(pageStart, pageStart + pageSize);
  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + pageSize, filtered.length);

  return (
    <WrapperSidebar>
      <HeaderRow>
        <Typography fontSize="18px" fontWeight={700} color="#101828">
          Deficiencies
        </Typography>
        <Stack direction="row" alignItems="center" gap="6px">
          <Typography fontSize="12px" color="#6B7280">
            {total} deficiencies{lastSyncedAt ? ` · synced ${formatSyncedAt(lastSyncedAt)}` : ''}
          </Typography>
          <RefreshOutlinedIcon sx={{ fontSize: '16px', color: '#6B7280', cursor: 'pointer' }} />
        </Stack>
      </HeaderRow>

      <FilterRow>
        <TextField
          size="small"
          placeholder="Search deficiencies..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{
            flex: 1,
            minWidth: 0,

            "& .MuiInputBase-root": {
              minWidth: 0,
            },

            "& .MuiInputBase-input": {
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },

            "& .MuiInputBase-input::placeholder": {
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small"
          value={tradeFilter}
          onChange={(e) => {
            setTradeFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 0 }}>
          <MenuItem value="ALL">All Trade Groups</MenuItem>
          {tradeOptions.map((trade) => (
            <MenuItem key={trade} value={trade}>
              {trade}
            </MenuItem> ))}
           {(Object.keys(TRADE_LABEL) as tradeGroup[]).map((trade) => (
            <MenuItem key={trade} value={trade}>
              {TRADE_LABEL[trade]}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 0 }}>
          <MenuItem value="ALL">All Statuses</MenuItem>
          {(Object.keys(STATUS_LABEL) as IssueStatus[]).map((status) => (
            <MenuItem key={status} value={status}>
              {STATUS_LABEL[status]}
            </MenuItem>
          ))}
        </Select>
      </FilterRow>

      <TableHead>
        <HeadCell sx={{ flex: '0 0 17%' }}>Deficiency ID</HeadCell>
        <HeadCell sx={{ flex: '0 0 10%' }}>Trade</HeadCell>
        <HeadCell sx={{ flex: 1 }}>Description</HeadCell>
        <HeadCell sx={{ flex: '0 0 17%' }}>Status</HeadCell>
        <HeadCell sx={{ flex: '0 0 18%' }}>Assignee</HeadCell>
        <HeadCell sx={{ flex: '0 0 10%', textAlign: 'right' }}>Action</HeadCell>
      </TableHead>

      <Stack sx={{ flex: 1, overflowY: 'auto' }}>
        {pageIssues.length === 0 && (
          <Box sx={{ padding: '24px', textAlign: 'center' }}>
            <Typography fontSize="13px" color="#6B7280">
              No deficiencies match your filters.
            </Typography>
          </Box>
        )}

        {pageIssues.map((issue) => {
          const isSelected = selectedId === issue.id;
          const tradeChar = tradeInitial(issue.tradeGroup);
          const tradeColor = TRADE_COLOR[tradeChar] || '#64748B';

          return (
            <Box key={issue.id}>
              <Row
                data-selected={isSelected}
                onClick={() => setSelectedId(isSelected ? null : issue.id)}>
                <Cell sx={{ flex: '0 0 17%' }}>
                  <Typography fontSize="13px" fontWeight={600} color="#101828">
                    {/* {formatDeficiencyId(issue.floorPlanId, issue.id)} */}
                    {issue.id}
                  </Typography>
                </Cell>
                <Cell sx={{ flex: '0 0 10%' }}>
                  <TradeBadge sx={{ backgroundColor: tradeColor }}>{tradeChar}</TradeBadge>
                </Cell>
                <Cell sx={{ flex: 1 }}>
                  <Typography fontSize="13px" color="#334155">
                    {issue.description || issue.title}
                  </Typography>
                </Cell>
                <Cell sx={{ flex: '0 0 17%' }}>
                  <StatusChip sx={{ color: STATUS_COLOR[issue.status] }}>
                    {STATUS_LABEL[issue.status]}
                  </StatusChip>
                </Cell>
                <Cell sx={{ flex: '0 0 18%' }}>
                  <Typography
                    fontSize="13px"
                    fontWeight={issue.actionBy ? 400 : 600}
                    color={issue.actionBy ? '#334155' : '#94A3B8'}>
                    {issue.actionBy || 'Unassigned'}
                  </Typography>
                </Cell>
                <Cell sx={{ flex: '0 0 10%', textAlign: 'right' }}>
                  <ViewLink
                    onClick={(e) => {
                      e.stopPropagation();
                      onView?.(issue);
                    }}>
                    View
                  </ViewLink>
                </Cell>
              </Row>

              {isSelected && (
                <ExpandedRow>
                  <ActionButton onClick={() => onAssign?.(issue)}>Assign</ActionButton>
                  <SecondaryButton onClick={() => onView?.(issue)}>Open Details</SecondaryButton>
                </ExpandedRow>
              )}
            </Box>
          );
        })}
      </Stack>

      <FooterRow>
        <Typography fontSize="13px" color="#6B7280">
          Showing {rangeStart} to {rangeEnd} of {filtered.length} deficiencies
        </Typography>
        <Stack direction="row" alignItems="center" gap="12px">
          <Stack direction="row" alignItems="center" gap="4px">
            <PageButton disabled={clampedPage <= 1} onClick={() => setPage(clampedPage - 1)}>
              <ChevronLeft/>
            </PageButton>
            {Array.from({ length: pageCount }, (_, i) => i + 1)
              .slice(0, 3)
              .map((p) => (
                <PageButton key={p} data-active={p === clampedPage} onClick={() => setPage(p)}>
                  {p}
                </PageButton>
              ))}
            <PageButton disabled={clampedPage >= pageCount} onClick={() => setPage(clampedPage + 1)}>
              <ChevronRight/>
            </PageButton>
          </Stack>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            sx={{ minWidth: '18%' }}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size}>
                {size} / page
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </FooterRow>
    </WrapperSidebar>
  );
};


const WrapperSidebar = styled(Stack)(() => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flexShrink: 0,
  height: '100%',
  borderLeft: '1px solid #E4E7EC',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
}));


const HeaderRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '16px 20px 12px',
  minWidth: 0,

  '& > *': {
    minWidth: 0,
  },
}));

const FilterRow = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: '8px',
  padding: '0 20px 12px',
  minWidth: 0,

  '& .MuiTextField-root': {
    minWidth: 0,
    flex: 1,
  },

  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

}));

const TableHead = styled(Stack)(() => ({
  flexDirection: 'row',
  padding: '10px 20px',
  borderTop: '1px solid #E4E7EC',
  borderBottom: '1px solid #E4E7EC',
  backgroundColor: '#F9FAFB',
  minWidth: 0,
}));

const HeadCell = styled(Box)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#667085',
  paddingRight: '8px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));


const Row = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: '12px 20px',
  borderBottom: '1px solid #F1F5F9',
  cursor: 'pointer',
  minWidth: 0,

  '&:hover': {
    backgroundColor: '#F8FAFC',
  },

  '&[data-selected="true"]': {
    backgroundColor: '#EEF2FF',
  },
}));

const Cell = styled(Box)(() => ({
  paddingRight: '8px',
  minWidth: 0,
  overflow: 'hidden',

  '& > *': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const TradeBadge = styled(Box)(() => ({
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
  fontSize: '12px',
  fontWeight: 700
}));

const StatusChip = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 500
}));

const ViewLink = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 600,
  color: '#2563EB',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline'
  }
}));

const ExpandedRow = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: '10px',
  padding: '0 20px 14px',
  backgroundColor: '#EEF2FF'
}));

const ActionButton = styled('button')(() => ({
  flex: 1,
  padding: '9px 14px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#1D4ED8',
  color: '#FFFFFF',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
}));

const SecondaryButton = styled('button')(() => ({
  flex: 1,
  padding: '9px 14px',
  borderRadius: '8px',
  border: '1px solid #C7D2FE',
  backgroundColor: '#FFFFFF',
  color: '#1D4ED8',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
}));

const FooterRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '14px 20px',
  borderTop: '1px solid #E4E7EC',
  minWidth: 0,

  '& > *': {
    minWidth: 0,
  },
}));

const PageButton = styled('button')(() => ({
  width: '30px',
  height: '30px',
  borderRadius: '6px',
  border: '1px solid #E4E7EC',
  backgroundColor: '#FFFFFF',
  color: '#334155',
  fontSize: '13px',
  cursor: 'pointer',
  '&:disabled': {
    color: '#CBD5E1',
    cursor: 'not-allowed'
  },
  '&[data-active="true"]': {
    backgroundColor: '#1D4ED8',
    borderColor: '#1D4ED8',
    color: '#FFFFFF'
  }
}));